import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { fetchVerifyCheckoutSession } from 'src/redux/employerUserSignup'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')

  const [verifying, setVerifying] = useState(true)
  const [isActive, setIsActive] = useState(false)
  const [preloadedJobCount, setPreloadedJobCount] = useState(0)
  const [employerName, setEmployerName] = useState(null)

  useEffect(() => {
    if (!sessionId) {
      return
    }

    const MAX_ATTEMPTS = 10
    const POLL_INTERVAL_MS = 3000
    let attempt = 0
    let cancelled = false

    const poll = async () => {
      while (attempt < MAX_ATTEMPTS && !cancelled) {
        attempt++
        const result = await dispatch(fetchVerifyCheckoutSession({ sessionId }))

        if (cancelled) return

        if (result?.isActive) {
          setIsActive(true)
          setPreloadedJobCount(result.preloadedJobCount ?? 0)
          setEmployerName(result.employerName ?? null)
          setVerifying(false)
          dispatch(
            fetchTrackEvent({
              eventName: 'checkout_completed',
              eventType: ProductAnalyticsEventTypes.ACTION,
              eventData: {
                preloadedJobCount: result.preloadedJobCount ?? 0,
                employerName: result.employerName,
              },
            }),
          )
          dispatch(
            fetchTrackEvent({
              eventName: 'signup_email_confirmed',
              eventType: ProductAnalyticsEventTypes.ACTION,
            }),
          )
          localStorage.removeItem('employerUserSignupWizard')
          return
        }

        if (attempt < MAX_ATTEMPTS) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS))
        }
      }

      if (!cancelled) {
        setVerifying(false)
        dispatch(
          fetchTrackEvent({
            eventName: 'checkout_failed',
            eventType: ProductAnalyticsEventTypes.ACTION,
            eventData: { attemptCount: attempt },
          }),
        )
      }
    }

    poll()

    return () => {
      cancelled = true
    }
  }, [dispatch, sessionId])

  if (!sessionId) {
    return <Navigate to='/' replace />
  }

  if (verifying) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 10,
          px: 2,
        }}
      >
        <CircularProgress size={48} sx={{ mb: 3 }} />
        <Typography variant='body1' color='text.secondary'>
          Verifying your subscription...
        </Typography>
      </Box>
    )
  }

  if (!isActive) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 10,
          px: 2,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 72, color: 'warning.main', mb: 2 }} />
        <Typography variant='h5' fontWeight={700} gutterBottom>
          Subscription Not Found
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 480, mb: 4 }}>
          We could not confirm your subscription. If you completed payment, please contact support.
        </Typography>
        <Button variant='contained' size='large' onClick={() => navigate('/')}>
          Go to Home
        </Button>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 10,
        px: 2,
      }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
      <Typography variant='h4' fontWeight={700} gutterBottom>
        Welcome to MyStethi Professional!
      </Typography>
      <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 480, mb: 4 }}>
        Your subscription is active. You now have full access to candidate matching and unlimited job postings.
      </Typography>
      {preloadedJobCount > 0 && (
        <Alert severity='info' sx={{ maxWidth: 480, mb: 4, textAlign: 'left' }}>
          We've already uploaded {preloadedJobCount} {preloadedJobCount === 1 ? 'job' : 'jobs'}
          {employerName ? ` for ${employerName}` : ''}. You can now view, edit, and manage
          {preloadedJobCount === 1 ? ' it' : ' them'} from your dashboard.
        </Alert>
      )}
      <Button variant='contained' size='large' onClick={() => navigate('/jobs')}>
        Go to Dashboard
      </Button>
    </Box>
  )
}

export default SubscriptionSuccessPage
